import type { TopLevelSpec } from "vega-lite";
import type { BaseChartSpecOptions, FieldType } from "../types.js";
import {
  numberFormatConfig,
  numberFormatProps,
  type NumberFormat,
} from "./format.js";
import { buildTitle, fieldRef } from "./shared.js";

export interface PieChartSpecOptions extends BaseChartSpecOptions {
  /** Field for each slice's category. */
  category: string;
  categoryType?: FieldType;
  /** Field for each slice's magnitude. */
  value: string;
  /**
   * Render as a donut. `true` uses an inner radius half the pie's; a number
   * sets the inner radius in pixels. `false` (default) renders a full pie.
   */
  donut?: boolean | number;
  /** Category and value names for the legend and tooltip. */
  categoryTitle?: string;
  valueTitle?: string;
  /**
   * Each slice's color, in the order of the data. Unset: the theme's
   * categorical colors, in the order of the category names.
   */
  sliceColors?: readonly string[];
  /** Draw the slices in the order of the data, clockwise from the top. */
  keepOrder?: boolean;
  /** Name each slice beside it (the legend is then left out). */
  labels?: boolean;
  /** How values read in the tooltip. */
  valueFormat?: NumberFormat;
}

/** The field holding each row's place in the data, for `keepOrder`. */
const ORDER_FIELD = "__vizOrder";

/** The pie's outer radius: room around it for labels when they are drawn. */
function outerRadius(labels: boolean): string {
  return `min(width, height) / 2 * ${labels ? 0.72 : 0.95}`;
}

function innerRadius(donut: boolean | number, outer: string) {
  if (donut === true) return { innerRadius: { expr: `(${outer}) * 0.5` } };
  return typeof donut === "number" ? { innerRadius: donut } : {};
}

/** Builds a Vega-Lite pie / donut chart spec. */
export function pieChartSpec(options: PieChartSpecOptions): TopLevelSpec {
  const {
    data,
    category,
    categoryType = "nominal",
    value,
    donut = false,
    title,
    subtitle,
    categoryTitle,
    valueTitle,
    sliceColors,
    keepOrder = false,
    labels = false,
    valueFormat,
  } = options;

  const outer = outerRadius(labels);
  const titleValue = buildTitle(title, subtitle);
  const values = data.map((row, index) => ({ ...row, [ORDER_FIELD]: index }));
  const categoryField = fieldRef(category);
  const valueField = fieldRef(value);
  const colorScale = sliceColors
    ? {
        scale: {
          domain: data.map((row) => row[category]),
          range: [...sliceColors],
        },
      }
    : {};
  const arc = {
    mark: {
      type: "arc" as const,
      radius: { expr: outer },
      ...innerRadius(donut, outer),
    },
  };
  const label = {
    mark: {
      type: "text" as const,
      radius: { expr: `${outer} + 22` },
      limit: { expr: `max(32, width / 2 - (${outer}) - 22)` },
    },
    encoding: { text: { field: categoryField, type: categoryType } },
  };

  return {
    data: { values },
    encoding: {
      theta: { field: valueField, type: "quantitative", stack: true },
      color: {
        field: categoryField,
        type: categoryType,
        ...(categoryTitle !== undefined ? { title: categoryTitle } : {}),
        ...colorScale,
        ...(labels ? { legend: null } : {}),
      },
      ...(keepOrder
        ? { order: { field: ORDER_FIELD, type: "quantitative" } }
        : {}),
      tooltip: [
        { field: categoryField, type: categoryType, title: categoryTitle },
        {
          field: valueField,
          type: "quantitative",
          title: valueTitle,
          ...numberFormatProps(valueFormat),
        },
      ],
    },
    layer: labels ? [arc, label] : [arc],
    view: { stroke: null },
    ...(titleValue !== undefined ? { title: titleValue } : {}),
    ...numberFormatConfig(valueFormat),
  };
}
